import { useEffect, useState } from 'react';
import {
  Plus, Search, Edit, Trash2, RotateCcw,
} from 'lucide-react';
import { TextInput } from '../../../shared/components/TextInput';
import { AutocompleteSelect } from '../../../shared/components/AutocompleteSelect';
import { Pagination } from '../../../shared/components/Pagination';
import { useCustodians, useAtualizarCustodianActive, useCustodianTypeOptions } from '../hooks';
import { CustodianCreateModal } from '../components/CustodianCreateModal';
import { CustodianEditModal } from '../components/CustodianEditModal';
import { opcoesDeTipoParaFiltro } from '../components/custodiaCadastro';
import { formatarData } from '../utils/formatters';
import { authService } from '../../../shared/lib/auth';
import { labelForCustodianType } from '../../../shared/utils/roleLabels';
import type { Custodian } from '../api';

const DEFAULT_PAGE_SIZE = 25;

type TypeFilter = 'all' | string;
type StatusFilter = 'all' | 'active' | 'inactive';

/**
 * Gestão de custodiários (responsáveis por ativos).
 *
 * Custodian é a entidade central do módulo Ativos que representa
 * "quem tem responsabilidade". Após o refactor, `type` espelha o
 * `user.role` — não é mais enum fechado. Slugs legados
 * (`department/project/client`) continuam sendo lidos para dados
 * antigos via `labelForCustodianType`.
 *
 * Layout alinhado ao padrão da tela de Usuários (UsersPage). A ação
 * "lixeira" aciona o toggle de ativação (desativar/reativar), não
 * exclusão física — mantém a semântica de RN005 com o ícone do padrão.
 */
export default function CustodiariosPage() {
  const user = authService.getCurrentUser();
  const canManage = user?.role === 'admin' || user?.role === 'governance';

  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState<TypeFilter>('all');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('active');
  const [limit, setLimit] = useState(DEFAULT_PAGE_SIZE);
  const [offset, setOffset] = useState(0);
  const [createOpen, setCreateOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<Custodian | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // Debounce da busca (300ms) — evita bater no backend a cada tecla.
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchTerm.trim()), 300);
    return () => clearTimeout(t);
  }, [searchTerm]);

  // Reset da paginação quando filtros mudam.
  useEffect(() => {
    setOffset(0);
  }, [debouncedSearch, typeFilter, statusFilter]);

  const query = useCustodians({
    type: typeFilter === 'all' ? undefined : typeFilter,
    active: statusFilter === 'all' ? undefined : statusFilter === 'active',
    search: debouncedSearch || undefined,
    limit,
    offset,
  });
  const toggle = useAtualizarCustodianActive();
  const tiposQuery = useCustodianTypeOptions();
  const filtrosDeTipo = opcoesDeTipoParaFiltro(tiposQuery.data);

  const items = query.data?.data ?? [];
  const count = query.data?.pagination?.count ?? 0;

  async function handleToggleActive(id: string, currentActive: boolean, name: string) {
    setErrorMessage(null);
    try {
      await toggle.mutateAsync({ id, active: !currentActive });
      setSuccessMessage(
        currentActive
          ? `"${name}" foi desativado. Alertas RN005 podem ser gerados se houver ativos atribuídos.`
          : `"${name}" foi reativado.`,
      );
      setTimeout(() => setSuccessMessage(null), 5000);
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { message?: string } } }).response?.data?.message;
      setErrorMessage(typeof msg === 'string' ? msg : 'Falha ao alterar status');
    }
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-gray-900">Custodiários</h1>
        {canManage && (
          <button
            onClick={() => setCreateOpen(true)}
            className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Plus className="w-5 h-5 mr-2" />
            Novo Custodiário
          </button>
        )}
      </div>

      {successMessage && (
        <div className="p-4 rounded-lg border bg-green-50 border-green-200">
          <p className="text-sm text-green-600">{successMessage}</p>
        </div>
      )}
      {errorMessage && (
        <div className="p-4 rounded-lg border bg-red-50 border-red-200">
          <p className="text-sm text-red-600">{errorMessage}</p>
        </div>
      )}

      <div className="max-w-md mb-4">
        <TextInput
          label="Buscar custodiários"
          icon={<Search className="w-5 h-5" />}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </div>

      <div className="flex flex-wrap items-end gap-4">
        <div className="min-w-[200px]">
          <AutocompleteSelect
            label="Tipo"
            options={[
              { value: 'all', label: 'Todos' },
              ...filtrosDeTipo,
            ]}
            value={typeFilter}
            onChange={(v) => setTypeFilter(v as TypeFilter)}
          />
        </div>
        <div className="min-w-[200px]">
          <AutocompleteSelect
            label="Status"
            options={[
              { value: 'all', label: 'Todos' },
              { value: 'active', label: 'Ativos' },
              { value: 'inactive', label: 'Inativos' },
            ]}
            value={statusFilter}
            onChange={(v) => setStatusFilter(v as StatusFilter)}
          />
        </div>
      </div>

      {query.isLoading ? (
        <div>Carregando...</div>
      ) : query.isError ? (
        <div className="text-red-600">Erro ao carregar custodiários.</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Nome</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Tipo</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Cargo / Descrição</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Contato</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Cadastrado em</th>
                {canManage && <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Ações</th>}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {items.length === 0 && (
                <tr>
                  <td colSpan={canManage ? 7 : 6} className="px-6 py-8 text-center text-sm text-gray-500">
                    Nenhum custodiário encontrado.
                  </td>
                </tr>
              )}
              {items.map((c) => {
                const isTogglePending = toggle.isPending && toggle.variables?.id === c.id;
                return (
                  <tr key={c.id} className={`hover:bg-gray-50 transition-colors ${c.active ? '' : 'bg-gray-50/60 opacity-70'}`}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{c.name}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-800">
                        {labelForCustodianType(c.type)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {c.jobTitle ?? '—'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-xs text-gray-500">
                      {c.email && <div>{c.email}</div>}
                      {c.phone && <div>{c.phone}</div>}
                      {!c.email && !c.phone && <span className="text-sm text-gray-500">—</span>}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {c.active ? (
                        <span className="px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800">
                          Ativo
                        </span>
                      ) : (
                        <span className="px-2 py-1 text-xs font-medium rounded-full bg-red-100 text-red-800">
                          Inativo
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatarData(c.createdAt)}
                    </td>
                    {canManage && (
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        <div className="flex space-x-2">
                          <button
                            onClick={() => setEditTarget(c)}
                            disabled={!c.active}
                            className="p-1 text-blue-600 hover:text-blue-900 hover:bg-blue-50 rounded transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                            title={c.active ? 'Editar custodiário' : 'Reative o custodiário para editar'}
                            aria-label={`Editar custodiário ${c.name}`}
                          >
                            <Edit className="w-4 h-4" />
                          </button>
                          {c.active ? (
                            <button
                              onClick={() => handleToggleActive(c.id, c.active, c.name)}
                              disabled={isTogglePending}
                              className="p-1 text-red-600 hover:text-red-900 hover:bg-red-50 rounded transition-colors disabled:opacity-50"
                              title="Desativar custodiário (RN005 — não exclui, apenas desativa)"
                              aria-label={`Desativar custodiário ${c.name}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          ) : (
                            <button
                              onClick={() => handleToggleActive(c.id, c.active, c.name)}
                              disabled={isTogglePending}
                              className="p-1 text-emerald-600 hover:text-emerald-900 hover:bg-emerald-50 rounded transition-colors disabled:opacity-50"
                              title="Reativar custodiário"
                              aria-label={`Reativar custodiário ${c.name}`}
                            >
                              <RotateCcw className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>

          <Pagination
            offset={offset}
            limit={limit}
            count={count}
            itemLabel="custodiário"
            onChange={({ offset: o, limit: l }) => { setOffset(o); setLimit(l); }}
          />
        </div>
      )}

      <CustodianCreateModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={() => {
          // Toast de sucesso é emitido pelo próprio CustodianCreateModal
          // via useToast (padrão adotado pós-refactor da develop). Aqui
          // apenas forçamos refetch como fallback defensivo além da
          // invalidação global feita pelo useCriarCustodian.
          query.refetch();
        }}
      />

      <CustodianEditModal
        open={editTarget !== null}
        custodian={editTarget}
        onClose={() => setEditTarget(null)}
        onSaved={() => {
          // O CustodianEditModal já emite toast de sucesso via useToast.
          query.refetch();
        }}
      />
    </div>
  );
}
